import { getSurveyAnswers } from "src/hooks/survey/answers";
import { useEnrollmentForm } from "src/hooks/enrollment";
import {
  useDateOfBirth,
  type UseDateOfBirthReturn,
} from "src/hooks/survey/date-of-birth";
import { addMainSurveyAnswers } from "src/utils/redux/slices/userData";
import type { AppDispatch } from "src/utils/redux/store";
import getStore from "src/utils/redux/store";
import { getMainSurveyUserDataNamespace } from "src/utils/survey/surveyNamespace";
import {
  formatDobToIsoDateString,
  resolveValidDisplayDob,
} from "src/utils/dobUtils";

/**
 * Checkout enrollment DOB field.
 *
 * - Source of truth: `paymentEnrollmentForm.enrollmentInfo.dateOfBirth`
 * - Prefill: survey `dateOfBirth` on mount
 * - UserData persistence: `persistCheckoutDateOfBirthToUserData` at purchase time
 */
export function useCheckoutDateOfBirth(): UseDateOfBirthReturn {
  const {
    enrollmentInfo,
    setField,
    enrollmentErrors,
    hasVisibleError: hasVisibleEnrollmentError,
  } = useEnrollmentForm();

  const onChange = (formatted: string) => setField({ dateOfBirth: formatted });

  const prefill = () => {
    if (resolveValidDisplayDob(enrollmentInfo.dateOfBirth)) {
      return;
    }

    const surveyDob = getSurveyDateOfBirthValue();
    if (surveyDob) {
      onChange(surveyDob);
    }
  };

  const core = useDateOfBirth({
    value: enrollmentInfo.dateOfBirth || "",
    onChange,
    prefill,
  });

  const enrollmentError =
    typeof enrollmentErrors.dateOfBirth === "string"
      ? enrollmentErrors.dateOfBirth
      : undefined;
  const showEnrollmentError = hasVisibleEnrollmentError("dateOfBirth");

  // Inline blur/format errors while typing; enrollment errors (e.g. med age) on submit.
  return {
    ...core,
    errorMessage:
      showEnrollmentError && enrollmentError
        ? enrollmentError
        : core.errorMessage,
  };
}

/**
 * Resolves DOB from survey sources only (display-normalized).
 *
 * Excludes the enrollment form so that a value the user typed into the checkout
 * field is never mistaken for previously collected data.
 */
export function getSurveyDateOfBirthValue(): string {
  const state = getStore().getState();
  const { surveyAnswers: surveyAnswersSlice } = state;
  const mergedSurveyAnswers = getSurveyAnswers(state);

  return (
    resolveValidDisplayDob(surveyAnswersSlice.dateOfBirth) ||
    resolveValidDisplayDob(mergedSurveyAnswers.dateOfBirth) ||
    resolveValidDisplayDob(surveyAnswersSlice.facialBloodScanDateOfBirth) ||
    resolveValidDisplayDob(mergedSurveyAnswers.facialBloodScanDateOfBirth)
  );
}

/**
 * Resolves checkout DOB for purchase and API sync.
 * Enrollment first, then survey sources (display-normalized).
 */
export function getCheckoutDateOfBirthValue(): string {
  const { paymentEnrollmentForm } = getStore().getState();

  return (
    resolveValidDisplayDob(paymentEnrollmentForm.enrollmentInfo.dateOfBirth) ||
    getSurveyDateOfBirthValue()
  );
}

/** Persists checkout DOB to UserData before publish. No-op when DOB is missing. */
export async function persistCheckoutDateOfBirthToUserData(
  dispatch: AppDispatch
): Promise<void> {
  const isoDate = formatDobToIsoDateString(getCheckoutDateOfBirthValue());
  if (!isoDate) {
    return;
  }

  const state = getStore().getState();
  const mainSurveyNameSpace = getMainSurveyUserDataNamespace();
  const existingDob = state.userData[mainSurveyNameSpace]?.dateOfBirth;

  if (formatDobToIsoDateString(existingDob) === isoDate) {
    return;
  }

  await dispatch(
    addMainSurveyAnswers({
      dateOfBirth: isoDate,
    })
  );
}
